import { IEbayMessage, IEbayMessageTemplate } from "../../models/EbayMessage";

/**
 * Starter set of canned replies. These live client-side until the
 * backend `/ebaymessaging/templates` endpoint ships — at that point
 * `EbayMessageChain` will pull user-editable templates and fall back
 * to these defaults when the call fails.
 *
 * Placeholder substitution is intentionally simple:
 *   `{{buyer}}`   → the eBay username of the sender
 *   `{{listing}}` → the listing title the message is attached to
 *   `{{item}}`    → the numeric eBay item id
 */
export const SAMPLE_EBAY_MESSAGE_TEMPLATES: IEbayMessageTemplate[] = [
  {
    id: "sample-thanks",
    name: "Thanks — will check",
    category: "General",
    body:
      "Hi {{buyer}},\n\nThanks for getting in touch about \"{{listing}}\". " +
      "We'll take a look and come back to you as soon as possible.\n\nKind regards,\nWilma",
  },
  {
    id: "sample-dispatched",
    name: "Dispatched today",
    category: "Dispatch",
    body:
      "Hi {{buyer}},\n\nGood news — your order for \"{{listing}}\" has been dispatched today. " +
      "Tracking will update within the next 24 hours.\n\nIf there's anything else we can help with just let us know.\n\nKind regards,\nWilma",
  },
  {
    id: "sample-fitment",
    name: "Fitment check",
    category: "Fitment",
    body:
      "Hi {{buyer}},\n\nTo confirm the part will fit your vehicle, please could you send across:\n" +
      "• Your registration (VRM)\n• The last 8 digits of your VIN\n\nWe'll verify compatibility against the listing and come straight back to you.\n\nKind regards,\nWilma",
  },
  {
    id: "sample-images",
    name: "Extra images",
    category: "General",
    body:
      "Hi {{buyer}},\n\nHappy to send additional images of the part. " +
      "Is there anything in particular you'd like us to photograph (connectors, casing, part numbers)?\n\nKind regards,\nWilma",
  },
  {
    id: "sample-return",
    name: "Return accepted",
    category: "Returns",
    body:
      "Hi {{buyer}},\n\nWe're sorry to hear the part isn't suitable. " +
      "We've accepted the return — please use the return label in your eBay order (item {{item}}) " +
      "and we'll process the refund as soon as the part arrives back with us.\n\nKind regards,\nWilma",
  },
  {
    id: "sample-outofstock",
    name: "Out of stock — refund",
    category: "Returns",
    body:
      "Hi {{buyer}},\n\nUnfortunately \"{{listing}}\" is no longer available. " +
      "We've cancelled the order and issued a full refund, which should appear on your statement within a few working days. " +
      "Apologies for the inconvenience.\n\nKind regards,\nWilma",
  },
];

/**
 * Replaces the supported placeholders (`{{buyer}}`, `{{listing}}`,
 * `{{item}}`) with values from the current message. Unknown
 * placeholders are left untouched so templates degrade gracefully.
 */
export function fillTemplate(
  template: IEbayMessageTemplate,
  message: IEbayMessage | undefined
): string {
  const lookup: Record<string, string> = {
    buyer: message?.sendingUserID ?? "there",
    listing: message?.itemTitle ?? "your item",
    item: message?.itemID ?? "",
  };
  return template.body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
    const value = lookup[key.toLowerCase()];
    return value ?? `{{${key}}}`;
  });
}
